import "./NavigationSideBarComponent.scss";
import {Observable, ReplaySubject} from "rxjs";
import {NavigationModelService} from "../../service/NavigationModelService";
import {IOnDestroy, IOnInit, IRootElementService} from "angular";
import {NavigationItems} from "../../models/NavigationItems";

export const NavigationSideBar = "tfNavigationSideBar";

export function navigationSideBar(parentDir: string) {
    return {
        templateUrl: `${parentDir}/side/NavigationSideBarComponent.html`,
        controller: NavigationSideBarComponent,
        controllerAs: "ctrl"
    };
}

const VisibleClass = "sidebar-visible";
const OpenClass = "sidebar-open";
const MobileSidebarClass = "mobile-sidebar";

export class NavigationSideBarComponent implements IOnInit, IOnDestroy {

    public navItems: Observable<NavigationItems>;
    public hasSidebar: Observable<boolean>;
    public isMobileNav: Observable<boolean>;

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly $element: IRootElementService,
                       private readonly navigationModelService: NavigationModelService) {

    }

    public $onInit(): void {
        this.navItems = this.navigationModelService
                            .sideNavModel
                            .takeUntil(this.destroyed$)
                            .map((model) => model.navigationItems);

        this.hasSidebar = this.navigationModelService.sidebarVisible;

        this.isMobileNav = this.navigationModelService
                               .isMobileNav
                               .takeUntil(this.destroyed$);

        const sidebarPinned$ = this.navigationModelService.sidebarPinned;
        const sidebarOpen$ = this.navigationModelService.sidebarOpen;

        Observable.combineLatest(sidebarOpen$, sidebarPinned$, this.isMobileNav)
                  .takeUntil(this.destroyed$)
                  .map(([open, pinned, isMobile]) => this.sidebarShouldBeOpen(open, pinned, isMobile))
                  .subscribe((open) => {
                      if (open) {
                          this.$element.addClass(OpenClass);
                      } else {
                          this.$element.removeClass(OpenClass);
                      }
                  });

        this.navigationModelService
            .sidebarVisible
            .takeUntil(this.destroyed$)
            .subscribe((visible) => {
                if (visible) {
                    this.$element.addClass(VisibleClass);
                } else {
                    this.$element.removeClass(VisibleClass);
                }
            });

        this.isMobileNav
            .takeUntil(this.destroyed$)
            .subscribe((isMobile) => {
                if (isMobile) {
                    this.$element.addClass(MobileSidebarClass);
                } else {
                    this.$element.removeClass(MobileSidebarClass);
                }
            });

        this.watchOutsideClicks();
    }

    private sidebarShouldBeOpen(sidebarOpen: boolean, sidebarPinned: boolean, isMobile: boolean) {
        if (sidebarOpen) {
            return true;
        } else if (isMobile) {
            return false;
        } else {
            return sidebarPinned;
        }
    }

    private watchOutsideClicks() {
        const handler = this.onOutsideClick.bind(this);

        this.navigationModelService
            .sidebarOpen
            .takeUntil(this.destroyed$)
            .subscribe((open) => {
                if (open) {
                    document.addEventListener("click", handler);
                } else {
                    document.removeEventListener("click", handler);
                }
            });
    }

    private onOutsideClick(event: Event) {
        const thisEl = this.$element[0];
        if (thisEl == null) {
            return;
        }
        const eventTarget = event.target as Node | null;

        if (eventTarget == null) {
            return;
        }

        if (!thisEl.contains(eventTarget)) {
            this.navigationModelService.closeSidebar();
        }
    }

    public open() {
        this.navigationModelService.openSidebar();
    }

    public close() {
        this.navigationModelService.closeSidebar();
    }

    public togglePinning() {
        this.navigationModelService.toggleSidebarPin();
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
